import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, effect, inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CompanyContextService } from '../../../../core/services/company-context.service';
import {
  SalesReportItem,
  SalesReportSummary
} from '../../data/models/sales-reports.models';
import { SalesReportsService } from '../../data/services/sales-reports.service';

@Component({
  selector: 'app-sales-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sales-reports.html',
  styleUrl: './sales-reports.scss',
  providers: [CurrencyPipe]
})
export class SalesReports implements OnDestroy {
  private readonly salesReportsService = inject(SalesReportsService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly currencyPipe = inject(CurrencyPipe);

  readonly selectedCompanyId = this.companyContextService.selectedCompanyId;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  searchTerm = '';
  summary: SalesReportSummary | null = null;
  reports: SalesReportItem[] = [];
  pageNumber = 1;
  pageSize = 1000;
  totalCount = 0;
  totalPages = 0;
  isLoading = false;
  errorMessage = '';

  showAlert = false;
  alertMessage = '';

  constructor() {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.pageNumber = 1;
      this.loadSalesReports();
    });

    effect(() => {
      const companyId = this.selectedCompanyId();
      if (companyId !== undefined) {
        this.pageNumber = 1;
        this.loadSalesReports();
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  onSearchChange(term: string): void {
    this.searchSubject.next(term);
  }

  loadSalesReports(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const params = {
      companyId: this.selectedCompanyId(),
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      search: this.searchTerm
    };

    this.salesReportsService.getSalesReports(params).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.summary = response.data.summary;
          this.reports = response.data.items;
          this.totalCount = response.data.totalCount;
          this.totalPages = response.data.totalPages;
        } else {
          this.errorMessage = response.message || 'Failed to load sales reports.';
        }
        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Unable to load sales report right now.';
        this.isLoading = false;
        this.showAlertMessage(this.errorMessage);
      }
    });
  }

  formatCurrency(amount: number): string {
    return this.currencyPipe.transform(amount, 'USD', 'symbol', '1.2-2') || '$0.00';
  }

  formatCompactCurrency(amount: number): string {
    if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'k';
    }
    return this.formatCurrency(amount);
  }

  getServiceCellText(enabled: boolean, amount: number): string {
    if (!enabled) return 'N';
    return `Y (${this.formatCurrency(amount)})`;
  }

  exportToCsv(): void {
    if (!this.reports || this.reports.length === 0) {
      this.showAlertMessage('No report data available to export.');
      return;
    }

    const rows = this.reports.map(report => ({
      'Job Name': report.jobName,
      'Pick Best': this.formatCurrency(report.pickBestSelected ? report.pickBestAmount : 0),
      'AI Enhancement': this.formatCurrency(report.aiEnhancementSelected ? report.aiEnhancementAmount : 0),
      'Cropping': this.formatCurrency(report.croppingSelected ? report.croppingAmount : 0),
      'Background Extraction': this.formatCurrency(
        report.backgroundExtractionSelected ? report.backgroundExtractionAmount : 0
      ),
      'Total Price': this.formatCurrency(report.totalPrice),
      'Advance Paid': this.formatCurrency(report.advancePaid),
      'Remaining Paid': this.formatCurrency(report.remainingPaid),
      'Total Collected': this.formatCurrency(report.totalCollected),
      'Refunded': this.formatCurrency(report.refundedAmount),
      'Outstanding': this.formatCurrency(report.outstandingAmount)
    }));

    const headers = Object.keys(rows[0]);

    const csvLines = [
      headers.join(','),
      ...rows.map(row =>
        headers
          .map(header => `"${String((row as any)[header] ?? '').replace(/"/g, '""')}"`)
          .join(',')
      )
    ];

    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `sales-report-${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  showAlertMessage(msg: string): void {
    this.alertMessage = msg;
    this.showAlert = true;
  }

  closeAlert(): void {
    this.showAlert = false;
  }
}